import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Star, Wrench, Bicycle, Lightning, Package } from "@phosphor-icons/react";
import api from "@/lib/apiClient";
import { useLang } from "@/contexts/LanguageContext";
import ProductCard from "@/components/ProductCard";
import useSeo from "@/hooks/useSeo";

const HERO_IMG = "https://images.pexels.com/photos/26757812/pexels-photo-26757812.jpeg";
const EBIKE_HERO = "https://images.pexels.com/photos/17381020/pexels-photo-17381020.jpeg";

const CATS = [
  { key: "new-bikes", label: "New Bikes", img: "https://images.pexels.com/photos/90454/pexels-photo-90454.jpeg", icon: Bicycle },
  { key: "e-bikes", label: "E-Bikes", img: "https://images.pexels.com/photos/15022766/pexels-photo-15022766.jpeg", icon: Lightning },
  { key: "used-bikes", label: "Used Bikes", img: "https://images.unsplash.com/photo-1485965120184-e220f721d03e?w=1200&q=85", icon: Bicycle },
  { key: "parts", label: "Parts", img: "https://images.unsplash.com/photo-1562615193-cbeef074a501?w=1200&q=85", icon: Wrench },
  { key: "accessories", label: "Accessories", img: "https://images.unsplash.com/photo-1557687650-2c17ee7fd58f?w=1200&q=85", icon: Package },
];

export default function HomePage() {
  useSeo({
    title: "Premium Bikes, E-Bikes & Parts",
    description: "VELOTEKA — Premium bikes, e-bikes, spare parts, service and rental. Imported from Germany, delivered across the Balkans. Since 2012, based in Strumica.",
    path: "/",
  });
  const { t } = useLang();
  const [featured, setFeatured] = useState([]);
  const [newArr, setNewArr] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [usedDeals, setUsedDeals] = useState([]);
  const [blog, setBlog] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products", { params: { new_arrival: true, limit: 4 } }).then((r) => setNewArr(r.data)).catch(() => {});
    api.get("/products", { params: { best_seller: true, limit: 4 } }).then((r) => setBestSellers(r.data)).catch(() => {});
    api.get("/products", { params: { condition: "used", limit: 4 } }).then((r) => setUsedDeals(r.data)).catch(() => {});
    api.get("/blog", { params: { limit: 3 } }).then((r) => setBlog(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[88vh] overflow-hidden grain">
        <img src={HERO_IMG} alt="Mountain bike hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex items-end pb-20">
          <div className="max-w-3xl fade-up">
            <div className="overline text-[color:var(--vt-orange)] mb-4 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="hero-overline">
              <span className="tracking-[0.3em]">SINCE 2012</span>
              <span className="w-1 h-1 rounded-full bg-[color:var(--vt-orange)] inline-block" />
              <span className="tracking-[0.3em]">REBRANDED 2019</span>
              <span className="w-1 h-1 rounded-full bg-[color:var(--vt-orange)] inline-block" />
              <span>{t.hero.overline}</span>
            </div>
            <h1 className="font-display font-black text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter mb-6">
              {t.hero.title}
            </h1>
            <p className="text-neutral-200 text-lg max-w-xl mb-8">{t.hero.subtitle}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop/new-bikes" className="vt-btn-orange" data-testid="hero-cta-new">
                {t.hero.shopNew} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/shop/e-bikes" className="vt-btn-outline text-white border-white" data-testid="hero-cta-ebikes">
                {t.hero.exploreEbikes}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute right-6 lg:right-12 bottom-8 z-10 text-white text-right hidden md:block">
          <div className="overline mb-2">EST. 2012 · STRUMICA</div>
          <div className="font-display font-black text-2xl">01 / 04</div>
        </div>
      </section>

      {/* HERITAGE STRIP */}
      <section className="bg-black text-white py-16 border-y border-white/5" data-testid="heritage-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-4 gap-8 items-center">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-2">/ HERITAGE</div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-none">
              Since<br />
              <span className="text-[color:var(--vt-orange)]">2012.</span>
            </div>
          </div>
          <div>
            <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">14</div>
            <div className="overline text-neutral-400">Years riding</div>
          </div>
          <div>
            <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">2019</div>
            <div className="overline text-neutral-400">Rebranded</div>
          </div>
          <div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              A small Strumica bike workshop that grew into the Balkans' most trusted online bicycle marketplace. Same passion, sharper edge.
            </p>
          </div>
        </div>
      </section>

      {/* BRAND MARQUEE */}
      <section className="py-10 border-y border-neutral-200 bg-white">
        <Marquee gradient={false} speed={40} pauseOnHover>
          {["TREK", "CANYON", "SPECIALIZED", "CUBE", "SCOTT", "GIANT", "BIANCHI", "MERIDA", "HAIBIKE", "RIESE & MÜLLER", "FOX", "SHIMANO", "SRAM", "BOSCH"].map((b, i) => (
            <span key={i} className="font-display font-black text-2xl md:text-3xl tracking-tighter mx-8 text-neutral-800 hover:text-[color:var(--vt-orange)] transition-colors">
              {b}
            </span>
          ))}
        </Marquee>
      </section>

      {/* CATEGORIES - TETRIS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex justify-between items-end mb-12">
          <div>
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ 01</div>
            <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{t.home.categories}</h2>
          </div>
        </div>
        <div className="grid grid-cols-6 grid-rows-2 gap-4 h-[520px]">
          {CATS.map((c, i) => (
            <Link
              key={c.key}
              to={`/shop/${c.key}`}
              data-testid={`home-cat-${c.key}`}
              className={`relative overflow-hidden group border border-black bg-neutral-100 ${
                i === 0 ? "col-span-3 row-span-2" : i === 1 ? "col-span-3 row-span-1" : "col-span-2 row-span-1"
              }`}
            >
              <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <h3 className="font-display font-black text-white text-3xl md:text-4xl tracking-tighter">{c.label}</h3>
                <ArrowRight size={28} weight="bold" className="text-white group-hover:text-[color:var(--vt-orange)] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <div className="flex justify-between items-end mb-10">
            <div>
              <div className="overline text-[color:var(--vt-orange)] mb-3">/ 02</div>
              <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{t.home.featured}</h2>
            </div>
            <Link to="/shop" className="link-underline text-sm font-semibold uppercase tracking-wider">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* E-BIKE PROMO */}
      <section className="relative h-[70vh] overflow-hidden my-16">
        <img src={EBIKE_HERO} alt="E-bike promo" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto h-full flex items-center px-6 lg:px-12">
          <div className="max-w-xl">
            <div className="overline text-[color:var(--vt-orange)] mb-4">/ E-MOBILITY</div>
            <h2 className="font-display font-black text-white text-5xl md:text-7xl tracking-tighter mb-4">
              Power<br />on demand.
            </h2>
            <p className="text-neutral-200 mb-6 text-lg">Bosch, Yamaha, Brose motors. Ranges up to 200 km. Made for the mountains and the streets of Strumica.</p>
            <Link to="/shop/e-bikes" className="vt-btn-orange" data-testid="ebike-promo-cta">
              Shop E-Bikes <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {newArr.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <div className="flex justify-between items-end mb-10">
            <div>
              <div className="overline text-[color:var(--vt-orange)] mb-3">/ 03</div>
              <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{t.home.newArrivals}</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {newArr.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* BEST SELLERS */}
      {bestSellers.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <div className="flex justify-between items-end mb-10">
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{t.home.bestSellers}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {bestSellers.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* USED DEALS */}
      {usedDeals.length > 0 && (
        <section className="dark-section py-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex justify-between items-end mb-10">
              <div>
                <div className="overline text-[color:var(--vt-orange)] mb-3">/ 04</div>
                <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{t.home.usedDeals}</h2>
              </div>
              <Link to="/shop/used-bikes" className="link-underline text-sm font-semibold uppercase tracking-wider text-white">View all →</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {usedDeals.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* MEET THE FOUNDER TEASER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="home-founder-teaser">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="aspect-[3/4] max-w-[420px] overflow-hidden border-4 border-[color:var(--vt-orange)] shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_ecommerce-veloteka/artifacts/modf6e11_IMG_20240818_141633.jpg"
                alt="VELOTEKA founder at Tumba peak"
                className="w-full h-full object-cover"
                data-testid="home-founder-img"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-black text-white px-4 py-3">
              <div className="overline text-[color:var(--vt-orange)]">Тумба · 1881m</div>
              <div className="font-display font-black">Belasica, MK</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="overline text-[color:var(--vt-orange)] mb-3">/ MEET THE FOUNDER</div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter mb-6 leading-[0.95]">
              14 years of<br />wrenches. One<br />workshop.
            </h2>
            <p className="text-neutral-700 text-lg mb-4 leading-relaxed max-w-xl">
              Hi, I'm <strong>Ivan</strong>. VELOTEKA is built on more than a decade of experience — from the bicycle club and service <strong>Aktiv in Strumica</strong> to years as a professional mechanic in <strong>Germany</strong>, servicing premium brands and modern e-bikes.
            </p>
            <p className="text-neutral-500 mb-8 max-w-xl">
              Certified. Trained. Straight advice, fair prices. Every bike gets the same attention.
            </p>
            <Link to="/about" className="vt-btn-primary" data-testid="home-founder-cta">
              Read Our Story <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">
        <div className="mb-12">
          <div className="overline text-[color:var(--vt-orange)] mb-3">/ 05</div>
          <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">{t.home.reviews}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: "Igor M.", from: "Strumica", body: "Ordered my Canyon Spectral and it arrived in 4 days. Perfectly assembled. Best bike shop in the Balkans." },
            { name: "Ana P.", from: "Ohrid", body: "The e-bike changed my life. Great service, real experts. They diagnosed a Bosch issue in minutes." },
            { name: "Stefan K.", from: "Bitola", body: "Prices better than most German shops when you factor in shipping. Real deal." },
          ].map((r, i) => (
            <div key={i} className="border border-neutral-200 p-8 hover:border-black transition-colors" data-testid={`review-card-${i}`}>
              <div className="flex mb-4 text-[color:var(--vt-orange)]">
                {[...Array(5)].map((_, k) => <Star key={k} size={16} weight="fill" />)}
              </div>
              <p className="text-neutral-800 mb-6 leading-relaxed">"{r.body}"</p>
              <div>
                <div className="font-bold">{r.name}</div>
                <div className="text-xs text-neutral-500">{r.from}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BLOG */}
      {blog.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
          <div className="flex justify-between items-end mb-10">
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter">{t.home.latestBlog}</h2>
            <Link to="/blog" className="link-underline text-sm font-semibold uppercase tracking-wider">All articles →</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {blog.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="group no-underline-link" data-testid={`home-blog-${post.slug}`}>
                <div className="aspect-[4/3] overflow-hidden mb-4">
                  <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="overline text-[color:var(--vt-orange)] mb-2">{post.category}</div>
                <h3 className="font-display font-black text-2xl tracking-tighter mb-2 group-hover:text-[color:var(--vt-orange)] transition-colors">{post.title}</h3>
                <p className="text-neutral-600 text-sm">{post.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
